import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig
} from "axios";

import type { ApiErrorState } from "../types/marketplace";

type RequestPrimitive = string | number | boolean | null | undefined;
export type RequestParams = Record<string, RequestPrimitive | RequestPrimitive[]>;
type ErrorBag = Record<string, string[]>;

interface ErrorResponseData {
  message?: string;
  errors?: ErrorBag | null;
}

export interface ApiRequestOptions<TParams extends RequestParams = RequestParams>
  extends Omit<AxiosRequestConfig, "data" | "method" | "params" | "url"> {
  includeClientId?: boolean;
  params?: TParams;
}

export class ApiRequestError extends Error {
  status?: number;
  errors?: ErrorBag | null;
  data?: unknown;

  constructor(message: string, status?: number, errors?: ErrorBag | null, data?: unknown) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.errors = errors;
    this.data = data;
  }
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_ENDPOINT ?? "http://localhost:8000/api/v1",
  timeout: 15000
});

const getAccessToken = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }

  return window.localStorage.getItem("accessToken");
};

const getClientId = (): string => process.env.REACT_APP_CLIENT_ID ?? "multi-tienda";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isErrorResponseData = (value: unknown): value is ErrorResponseData => {
  if (!isRecord(value)) {
    return false;
  }

  const message = value.message;
  const errors = value.errors;

  return (
    (message === undefined || typeof message === "string") &&
    (errors === undefined || errors === null || isRecord(errors))
  );
};

const getErrorMessage = (error: AxiosError<ErrorResponseData>): string => {
  const responseMessage = error.response?.data?.message;

  if (responseMessage) {
    return responseMessage;
  }

  if (error.message) {
    return error.message;
  }

  return "Request error";
};

export const normalizeApiError = (error: unknown): ApiRequestError => {
  if (axios.isAxiosError(error)) {
    const responseData = isErrorResponseData(error.response?.data)
      ? error.response?.data
      : undefined;
    const typedError = error as AxiosError<ErrorResponseData>;

    return new ApiRequestError(
      getErrorMessage(typedError),
      typedError.response?.status,
      responseData?.errors ?? null,
      typedError.response?.data
    );
  }

  if (error instanceof Error) {
    return new ApiRequestError(error.message);
  }

  return new ApiRequestError("Request error");
};

export const toApiErrorState = (error: unknown): ApiErrorState => {
  const apiError = normalizeApiError(error);

  return {
    message: apiError.message,
    status: apiError.status,
    errors: apiError.errors ?? null
  };
};

const withClientId = <TParams extends RequestParams>(
  params: TParams | undefined,
  includeClientId = true
): RequestParams => {
  const mergedParams: RequestParams = params ? { ...params } : {};

  if (includeClientId) {
    mergedParams.client_id = getClientId();
  }

  return mergedParams;
};

const withClientIdBody = <TBody>(
  body: TBody | undefined,
  includeClientId = true
): TBody | Record<string, unknown> | FormData | undefined => {
  if (!includeClientId) {
    return body;
  }

  if (body === undefined) {
    return { client_id: getClientId() };
  }

  if (typeof FormData !== "undefined" && body instanceof FormData) {
    body.append("client_id", getClientId());
    return body;
  }

  if (isRecord(body)) {
    return {
      ...body,
      client_id: getClientId()
    };
  }

  return body;
};

const request = async <TResponse>(
  config: AxiosRequestConfig
): Promise<TResponse> => {
  try {
    const response: AxiosResponse<TResponse> = await api.request<TResponse>(config);
    return response.data;
  } catch (error: unknown) {
    throw normalizeApiError(error);
  }
};

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();

  config.headers.set("Accept", "application/json");

  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }

  return config;
});

export const get = async <
  TResponse,
  TParams extends RequestParams = RequestParams
>(
  url: string,
  options: ApiRequestOptions<TParams> = {}
): Promise<TResponse> => {
  const { includeClientId = true, params, ...axiosOptions } = options;

  return request<TResponse>({
    ...axiosOptions,
    method: "GET",
    params: withClientId(params, includeClientId),
    url
  });
};

export const post = async <TResponse, TBody = unknown>(
  url: string,
  body?: TBody,
  options: ApiRequestOptions = {}
): Promise<TResponse> => {
  const { includeClientId = true, params, ...axiosOptions } = options;

  return request<TResponse>({
    ...axiosOptions,
    data: withClientIdBody(body, includeClientId),
    method: "POST",
    params: withClientId(params, false),
    url
  });
};

export const put = async <TResponse, TBody = unknown>(
  url: string,
  body?: TBody,
  options: ApiRequestOptions = {}
): Promise<TResponse> => {
  const { includeClientId = true, params, ...axiosOptions } = options;

  return request<TResponse>({
    ...axiosOptions,
    data: withClientIdBody(body, includeClientId),
    method: "PUT",
    params: withClientId(params, false),
    url
  });
};

export const patch = async <TResponse, TBody = unknown>(
  url: string,
  body?: TBody,
  options: ApiRequestOptions = {}
): Promise<TResponse> => {
  const { includeClientId = true, params, ...axiosOptions } = options;

  return request<TResponse>({
    ...axiosOptions,
    data: withClientIdBody(body, includeClientId),
    method: "PATCH",
    params: withClientId(params, false),
    url
  });
};

export const del = async <
  TResponse,
  TParams extends RequestParams = RequestParams
>(
  url: string,
  options: ApiRequestOptions<TParams> = {}
): Promise<TResponse> => {
  const { includeClientId = true, params, ...axiosOptions } = options;

  return request<TResponse>({
    ...axiosOptions,
    method: "DELETE",
    params: withClientId(params, includeClientId),
    url
  });
};
